import { TradePairTag } from '@ui/constants/tokens/tradePairTags';
import { PairResponsePairTypeEnum } from '@ui/services/backend/generated';
import { ChainName } from '@ui/types/chain';
import { BareTradePair } from '@ui/types/tradePair';
import { TFunction } from 'next-i18next';

export enum CategoryTag {
  // Basic tags
  ALL = 'ALL',
  FAVORITES = 'FAVORITES',
  // Main categories
  CRYPTO = 'CRYPTO',
  STOCKS = 'STOCKS',
  FOREX = 'FOREX',
  INDICES = 'INDICES',
  COMMODITIES = 'COMMODITIES',
}

const DEFAULT_CATEGORY_TAGS: CategoryTag[] = [
  CategoryTag.ALL,
  CategoryTag.FAVORITES,
  CategoryTag.CRYPTO,
  CategoryTag.STOCKS,
  CategoryTag.FOREX,
  CategoryTag.INDICES,
  CategoryTag.COMMODITIES,
];

export const CATEGORY_TAGS: Record<ChainName, CategoryTag[]> = {
  [ChainName.MONAD]: DEFAULT_CATEGORY_TAGS,
  [ChainName.MONAD_TESTNET]: DEFAULT_CATEGORY_TAGS,
};

export const getSubCategoryTags = (
  categoryTag: CategoryTag,
  pairs: BareTradePair[],
): TradePairTag[] => {
  switch (categoryTag) {
    case CategoryTag.CRYPTO: {
      // the default tags and order, to align current design
      const set = new Set<TradePairTag>([
        'L1', // This will be default when CRYPTO is selected
        'L2',
        'AI',
        'DeFi',
        'Gaming',
        'Meme',
        'DAO',
      ]);

      // include new tags that is added into crypto pairs
      pairs
        .filter((p) => p.type === PairResponsePairTypeEnum.Crypto)
        .reduce((acc, pair) => {
          pair.tags.forEach((tag) => acc.add(tag));

          return acc;
        }, set);

      return Array.from(set.values());
    }
    case CategoryTag.STOCKS: {
      // the default tags and order, to align current design
      const set = new Set<TradePairTag>(['Stock']);
      pairs
        .filter((pair) =>
          [
            PairResponsePairTypeEnum.Stocks,
            PairResponsePairTypeEnum.Forex,
            PairResponsePairTypeEnum.Commodities,
            PairResponsePairTypeEnum.Indices,
          ].includes(pair.type),
        )
        .reduce((acc, pair) => {
          pair.tags.forEach((tag) => acc.add(tag));

          return acc;
        }, set);
      // the default tags and order, to align current design

      return Array.from(set.values());
    }
    case CategoryTag.FOREX: {
      const set = new Set<TradePairTag>(['Forex']);

      pairs
        .filter((pair) => [PairResponsePairTypeEnum.Forex].includes(pair.type))
        .reduce((acc, pair) => {
          pair.tags.forEach((tag) => acc.add(tag));

          return acc;
        }, set);

      return Array.from(set.values());
    }
    case CategoryTag.INDICES: {
      const set = new Set<TradePairTag>(['Indices']);
      pairs
        .filter((pair) => [PairResponsePairTypeEnum.Indices].includes(pair.type))
        .reduce((acc, pair) => {
          pair.tags.forEach((tag) => acc.add(tag));

          return acc;
        }, set);

      return Array.from(set.values());
    }
    case CategoryTag.ALL:
    case CategoryTag.COMMODITIES:
      const set = new Set<TradePairTag>(['Commodities']);
      pairs
        .filter((pair) =>
          [PairResponsePairTypeEnum.Commodities].includes(pair.type),
        )
        .reduce((acc, pair) => {
          pair.tags.forEach((tag) => acc.add(tag));

          return acc;
        }, set);
    case CategoryTag.FAVORITES:
      return [] as TradePairTag[];
  }
};

export const getCategoryTagLabels = (
  t: TFunction,
): Record<CategoryTag, string> => ({
  [CategoryTag.ALL]: t('common.All'),
  [CategoryTag.FAVORITES]: t('common.Favorites'),
  [CategoryTag.CRYPTO]: t('common.Crypto'),
  [CategoryTag.STOCKS]: t('common.Stocks'),
  [CategoryTag.FOREX]: t('common.Forex'),
  [CategoryTag.INDICES]: t('common.Indices'),
  [CategoryTag.COMMODITIES]: t('common.Commodities'),
});

export const COMING_SOON_TAGS: TradePairTag[] = []; // remove once do not need
